import React, { useState, useRef, useEffect } from "react";

export interface ParsedFilter {
  key: string;
  value: string;
  op?: ":" | "=" | ">" | "<" | ">=" | "<=";
}

interface GenericSearchbarProps {
  placeholder?: string;
  suggestedKeys?: string[];
  onFiltersChange: (filters: ParsedFilter[]) => void;
  liveFiltering?: boolean;
  debounceMs?: number;
  size?: SearchbarSize;
  disabled?: boolean;
  // Server-side search mode
  serverSideSearch?: boolean;
  onServerSideSearchChange?: (searchValue: string) => void;
  serverSideDebounceMs?: number;
  // Custom input max-width (CSS value like "200px", "300px", "100%", or Tailwind class like "max-w-md")
  inputMaxWidth?: string;
  /** Fires when the count of committed filter chips (Enter-key) changes. */
  onCommittedFilterCountChange?: (count: number) => void;
  /**
   * Optional node pinned inside the input's right edge — e.g. a live result count, so a caller
   * can put it in the field itself instead of spending a toolbar row on a separate count line.
   * Sits outside the `flex-1` input and never shrinks, so long labels squeeze the typing area
   * rather than overflowing the border.
   */
  trailingContent?: React.ReactNode;
}

const parseInputToFilter = (input: string): ParsedFilter => {
  const match = input.match(/^\s*(.+?)\s*[:=]\s*(.+?)\s*$/);
  if (match) return { key: match[1], value: match[2] };
  return { key: "", value: input.trim() };
};

export type SearchbarSize = "xs" | "sm" | "md";

const sizeHeightClasses: Record<SearchbarSize, string> = {
  xs: "h-8",
  sm: "h-9",
  md: "h-10",
};

/** Padding tuned to match common control heights (e.g. GenericDropdown sm = h-8). */
const sizePaddingClasses: Record<SearchbarSize, string> = {
  xs: "px-2 py-0",
  sm: "px-3 py-1.5",
  md: "px-3 py-1.5",
};

const GenericSearchbar: React.FC<GenericSearchbarProps> = ({
  placeholder = "Find by attribute or value (case-sensitive)",
  suggestedKeys = [],
  onFiltersChange,
  liveFiltering = true,
  debounceMs = 200,
  size = "md",
  disabled = false,
  serverSideSearch = false,
  onServerSideSearchChange,
  serverSideDebounceMs = 600,
  inputMaxWidth = "200px",
  onCommittedFilterCountChange,
  trailingContent,
}) => {
  const [input, setInput] = useState("");
  const [filters, setFilters] = useState<ParsedFilter[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const [liveFilter, setLiveFilter] = useState<ParsedFilter | null>(null);
  const [hasChosenKey, setHasChosenKey] = useState(false);

  const onFiltersChangeRef = useRef(onFiltersChange);
  onFiltersChangeRef.current = onFiltersChange;

  const onCommittedFilterCountChangeRef = useRef(
    onCommittedFilterCountChange
  );
  onCommittedFilterCountChangeRef.current = onCommittedFilterCountChange;

  useEffect(() => {
    onCommittedFilterCountChangeRef.current?.(filters.length);
  }, [filters.length]);

  const leftSide =
    input.split(/[=:]/, 1)[0]?.trim().toLowerCase() ||
    input.trim().toLowerCase();
  const filteredKeys = suggestedKeys.filter((key) =>
    key.toLowerCase().includes(leftSide)
  );

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced live typing -> update ONLY local liveFilter
  useEffect(() => {
    if (!liveFiltering) return;

    if (debounceTimer.current !== null) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }

    debounceTimer.current = setTimeout(() => {
      const lf = parseInputToFilter(input);
      if (hasChosenKey && lf.key && lf.value.trim()) {
        // live contains while typing
        setLiveFilter({ ...lf, op: ":" });
      } else {
        setLiveFilter(null);
      }
    }, debounceMs);

    return () => {
      if (debounceTimer.current !== null) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
    };
  }, [input, liveFiltering, debounceMs, hasChosenKey]);

  // The ONLY place we notify the parent (post-render). Use ref so parent callback identity doesn't cause infinite loop.
  useEffect(() => {
    if (liveFiltering && liveFilter && liveFilter.value.trim()) {
      onFiltersChangeRef.current([...filters, liveFilter]);
    } else {
      onFiltersChangeRef.current(filters);
    }
  }, [filters, liveFilter, liveFiltering]);

  // Server-side search: debounce input changes and trigger search while typing
  const serverSideDebounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousInputRef = useRef<string>('');
  useEffect(() => {
    if (!serverSideSearch || !onServerSideSearchChange) return;

    // Clear any pending debounced call
    if (serverSideDebounceTimer.current !== null) {
      clearTimeout(serverSideDebounceTimer.current);
      serverSideDebounceTimer.current = null;
    }

    // If input is cleared (was not empty, now is empty), immediately clear the search value
    if (!input.trim() && previousInputRef.current.trim()) {
      onServerSideSearchChange('');
      previousInputRef.current = input;
      return;
    }

    // If input is empty and was already empty, don't trigger (prevents unnecessary calls)
    if (!input.trim()) {
      previousInputRef.current = input;
      return;
    }

    // Extract general search value from input (only when no key is specified)
    // If input contains "key = value" or "key: value", extract just the value part for server-side search
    const parsed = parseInputToFilter(input);
    const hasKey = parsed.key && parsed.key.trim() !== '';
    
    // For server-side search: use the value part (or entire input if no key)
    // This allows searching across all columns on the backend
    const searchValue = hasKey ? parsed.value.trim() : input.trim();
    
    serverSideDebounceTimer.current = setTimeout(() => {
      onServerSideSearchChange(searchValue);
      previousInputRef.current = input;
    }, serverSideDebounceMs);

    return () => {
      if (serverSideDebounceTimer.current !== null) {
        clearTimeout(serverSideDebounceTimer.current);
        serverSideDebounceTimer.current = null;
      }
    };
  }, [input, serverSideSearch, onServerSideSearchChange, serverSideDebounceMs]);

  // Commit on Enter — no parent call here
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && input.trim() !== "") {
      const f = parseInputToFilter(input.trim());
      setFilters((prev) => [...prev, { key: f.key, value: f.value }]);
      
      // Clear any pending debounced call to prevent it from firing after input is cleared
      if (serverSideSearch && onServerSideSearchChange) {
        if (serverSideDebounceTimer.current !== null) {
          clearTimeout(serverSideDebounceTimer.current);
          serverSideDebounceTimer.current = null;
        }
        // Only trigger server-side search if it's a general search (no key)
        // Key-value pairs are now in the filters array and will be included in the filter query
        const hasKey = f.key && f.key.trim() !== '';
        if (!hasKey) {
          // General search - trigger immediately
          onServerSideSearchChange(f.value.trim());
        } else {
          // Key-value pair - clear the debounced search value since it's now in filters
          onServerSideSearchChange('');
        }
      }
      
      setInput("");
      setLiveFilter(null);
      setHasChosenKey(false);
      setShowSuggestions(false);
    } else if (e.key === "Escape") {
      setInput("");
      setLiveFilter(null);
      setHasChosenKey(false);
      setShowSuggestions(false);
      // parent is updated by the effect
    }
  };

  const handleRemoveFilter = (idx: number) => {
    setFilters((prev) => prev.filter((_, i) => i !== idx));
    // parent is updated by the effect
  };

  const handleSuggestionClick = (key: string) => {
    setHasChosenKey(true);
    setInput(key + " = ");
    setShowSuggestions(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  useEffect(() => {
    if (input.trim() === "") setHasChosenKey(false);
  }, [input]);

  return (
    <div className="w-full relative">
      <div
        className={`flex min-w-0 w-full items-center rounded-sm border border-semantic-border-secondary bg-semantic-bg-primary-alt ${size === "xs" ? "" : "shadow-sm"} ${sizePaddingClasses[size]} ${sizeHeightClasses[size]} ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
      >
        <svg
            className={`flex-shrink-0 text-semantic-text-secondary ${
              size === "xs" ? "mr-1.5 h-3.5 w-3.5" : "mr-2 h-4 w-4"
            } ${disabled ? "opacity-50" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z"
            />
          </svg>
        <input
          ref={inputRef}
          type="text"
          className={`flex-1 min-w-0 outline-none bg-transparent text-semantic-text-primary placeholder-gray-400 ${
            size === "xs" ? "text-xs" : "text-sm"
          } ${
            // If it's a Tailwind class (starts with max-w-), add it to className
            inputMaxWidth.startsWith("max-w-") ? inputMaxWidth : ""
          }`}
          // className="flex-1 min-w-0 outline-none bg-transparent text-semantic-text-primary placeholder-gray-400 text-sm"
          placeholder={placeholder}
          value={input}
          disabled={disabled}
                    style={{
            // If it's a CSS value (px, %, rem, em, vw), apply via inline style
            maxWidth: inputMaxWidth.includes("px") || inputMaxWidth.includes("%") || inputMaxWidth.includes("rem") || inputMaxWidth.includes("em") || inputMaxWidth.includes("vw")
              ? inputMaxWidth
              : inputMaxWidth.startsWith("max-w-")
              ? undefined
              : inputMaxWidth, // Fallback: treat as CSS value if it doesn't match patterns
          }}
          onChange={(e) => {
            if (!disabled) {
              setInput(e.target.value);
              setShowSuggestions(true);
            }
          }}
          onFocus={() => {
            if (!disabled) {
              setShowSuggestions(true);
            }
          }}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 100)}
          onKeyDown={handleInputKeyDown}
        />
        {trailingContent && (
          <div
            className={`ml-2 flex-shrink-0 whitespace-nowrap text-semantic-text-secondary ${
              size === "xs" ? "text-xs" : "text-sm"
            }`}
          >
            {trailingContent}
          </div>
        )}
      </div>

      {!disabled && showSuggestions && leftSide && filteredKeys.length > 0 && (
        <div className="absolute mt-1 bg-semantic-bg-primary-alt border border-semantic-border-primary rounded-sm shadow-lg z-50 max-h-60 overflow-auto w-full left-0 right-0">
          <div className="px-3 py-2 text-xs text-semantic-text-secondary border-b border-semantic-border-secondary">
            Suggested filters
          </div>
          {filteredKeys.map((key) => (
            <div
              key={key}
              className="px-3 py-2 cursor-pointer hover:bg-semantic-state-active text-semantic-text-secondary text-sm transition-colors"
              onMouseDown={() => handleSuggestionClick(key)}
            >
              {key} =
            </div>
          ))}
        </div>
      )}

      {filters.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {filters.map((f, idx) => (
            <span
              key={idx}
              className="inline-flex max-w-full items-center rounded-full bg-semantic-bg-tertiary px-2 py-1 text-xs text-semantic-text-secondary"
            >
              {f.key ? (
                <>
                  <span className="truncate font-semibold">{f.key}</span>
                  <span className="mx-1 flex-shrink-0">:</span>
                  <span className="truncate">{f.value}</span>
                </>
              ) : (
                <span className="truncate">{f.value}</span>
              )}
              <button
                className="ml-2 flex-shrink-0 text-semantic-text-secondary hover:text-semantic-text-danger"
                onClick={() => handleRemoveFilter(idx)}
                aria-label="Remove filter"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
export default GenericSearchbar;